import {
  Account,
  Facebook,
  Instagram,
  Twitter,
  UAGLogo,
  Youtube,
} from "@components/icons"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { smallcapsBold13Styles } from "@constants/typography"
import { DesktopOnly } from "@utils/MediaQuery"
import { uniqueId } from "lodash-es"
import React from "react"
import styled from "styled-components"

import {
  Copyright,
  ExternalFooterLinkComponent,
  Footer,
  FooterLink,
  FooterLinkProps,
  FooterProps,
  LegalLink,
  Logo,
  LogoWrapper,
  Main,
  Social,
  SocialIcon,
  SocialIconWrapper,
  User,
  UserIcon,
} from "./common"

const Animation = styled.div`
  position: relative;
`

const Dash = styled.div`
  width: 26px;
  height: 1px;
  background-color: ${colors.offBlack};
  transform-origin: right;
  transition:
    background-color ${animation.quick} ${cubicBezier} 150ms,
    transform 350ms cubic-bezier(0.24, 0, 0.18, 1.5) 100ms;
`

const TopLine = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  width: 7px;
  height: 1px;
  background-color: ${colors.offBlack};
  transform-origin: right;
  transform: translateX(0) rotate(45deg) scaleX(0);
  transition:
    background-color ${animation.quick} ${cubicBezier} 150ms,
    transform 200ms cubic-bezier(0.5, 0.15, 0, 1);
`

const BottomLine = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  width: 7px;
  height: 1px;
  background-color: ${colors.offBlack};
  transform-origin: right;
  transform: translateX(0) rotate(-45deg) scaleX(0);
  transition:
    background-color ${animation.quick} ${cubicBezier} 150ms,
    transform 200ms cubic-bezier(0.5, 0.15, 0, 1);
`

const hoverProps = `
  ${Dash} {
    background-color: ${colors.offWhite};
    transform: scaleX(0.6);
    transition: background-color ${animation.quick} ${cubicBezier}, transform 100ms cubic-bezier(0.5, 0.15, 0, 1);
  }

  ${TopLine} {
    background-color: ${colors.offWhite};
    transform: translateX(1px) rotate(45deg) scaleX(1);
    transition: background-color ${animation.quick} ${cubicBezier}, transform 400ms cubic-bezier(0, 1.4, 0.5, 1) 60ms;
  }

  ${BottomLine} {
    background-color: ${colors.offWhite};
    transform: translateX(1px) rotate(-45deg) scaleX(1);
    transition: background-color ${animation.quick} ${cubicBezier}, transform 400ms cubic-bezier(0, 1.4, 0.5, 1) 90ms;
  }
`

export const animationStyles = `
  @media ${breakpoints.laptop} {
    &:hover {
      cursor: pointer;
      ${hoverProps}
    }
  }
`

const Column = styled.div`
  display: flex;
  width: calc(2 / 12 * 100vw);
  flex-direction: column;
  flex-grow: 0;
  flex-shrink: 0;
`

const disabledProps = `
  pointer-events: none;

  @media ${breakpoints.laptop} {
    &:hover {
      cursor: initial;
    }
  }
`
const ColumnHeader = styled.a<{ $animated?: boolean }>`
  ${smallcapsBold13Styles}
  display: flex;
  height: 54px;
  margin-bottom: 16px;
  align-items: center;
  color: ${colors.offWhite};

  ${({ $animated }) => ($animated ? animationStyles : disabledProps)}
`

const ColumnHeaderText = styled.span`
  margin-right: 20px;
  padding-top: 4px;
`

const Sub = styled.div`
  display: flex;
  margin-bottom: 59px;
`

const currentYear = new Date().getFullYear()

const DesktopFooter: React.FC<FooterProps> = ({
  footerBusinessLabelLink,
  footerBusinessLinks,
  footerExploreLabelLink,
  footerExploreLinks,
  footerShopLabelLink,
  footerShopLinks,
  footerSupportLabelLink,
  footerSupportLinks,
  legalLinks,
  socialLinks,
  user,
}) => {
  const renderColumnHeader = (link: FooterLinkProps) => {
    const externalProps: any = {}
    if (link.external) {
      externalProps.rel = "noopener"
    }
    if (link.openNewTab) {
      externalProps.target = "_blank"
    }
    return (
      <ColumnHeader
        $animated={(link.link && link.link.length > 0) || false}
        href={link.link || "/"}
        {...externalProps}
      >
        <ColumnHeaderText>{link.text}</ColumnHeaderText>
        <Animation>
          <Dash />
          <TopLine />
          <BottomLine />
        </Animation>
      </ColumnHeader>
    )
  }

  const renderFooterLinks = (links: Array<FooterLinkProps>) =>
    links.length > 0 &&
    links.map((link: FooterLinkProps) => (
      <FooterLink key={uniqueId()} {...link} />
    ))

  const renderLegalLink = (legalUrl, legalText) =>
    legalUrl && (
      <Column>
        <LegalLink to={legalUrl}>{legalText}</LegalLink>
      </Column>
    )

  const renderSocialLink = (
    socialUrl: string | undefined,
    IconComponent: React.ReactNode,
    props?: React.AnchorHTMLAttributes<HTMLAnchorElement>
  ) =>
    socialUrl && (
      <SocialIconWrapper
        href={socialUrl}
        rel="noopener"
        target="_blank"
        {...props}
      >
        <SocialIcon>{IconComponent}</SocialIcon>
      </SocialIconWrapper>
    )

  const { facebook, instagram, twitter, youtube } = socialLinks || {}

  const { limitationsOfLiability, privacyPolicy, termsAndConditions } =
    legalLinks || {}

  return (
    <DesktopOnly>
      <Footer>
        <Main>
          <Column>
            <LogoWrapper>
              <Logo to="/">
                <UAGLogo />
              </Logo>
            </LogoWrapper>
          </Column>
          <Column>
            {footerShopLabelLink && renderColumnHeader(footerShopLabelLink)}
            {footerShopLinks && renderFooterLinks(footerShopLinks)}
          </Column>
          <Column>
            {footerExploreLabelLink &&
              renderColumnHeader(footerExploreLabelLink)}
            {footerExploreLinks && renderFooterLinks(footerExploreLinks)}
          </Column>
          <Column>
            {footerBusinessLabelLink &&
              renderColumnHeader(footerBusinessLabelLink)}
            {footerBusinessLinks && renderFooterLinks(footerBusinessLinks)}
          </Column>
          <Column>
            {footerSupportLabelLink &&
              renderColumnHeader(footerSupportLabelLink)}
            {footerSupportLinks && renderFooterLinks(footerSupportLinks)}
            <ExternalFooterLinkComponent
              className="termly-display-preferences"
              href="#"
              id="desktopTermlyButton"
            >
              <span>Consent Preferences</span>
            </ExternalFooterLinkComponent>
          </Column>
        </Main>
        <Sub>
          <Column>
            <User to="/account">
              <UserIcon>
                <Account />
              </UserIcon>
              {user}
            </User>
          </Column>
          {renderLegalLink(privacyPolicy, "Privacy Policy")}
          {renderLegalLink(termsAndConditions, "Terms and Conditions")}
          {renderLegalLink(limitationsOfLiability, "Limitations of Liability")}
          <Column>
            <Social>
              {renderSocialLink(instagram, <Instagram />, {
                "aria-label": "Instagram",
              })}
              {renderSocialLink(twitter, <Twitter />, {
                "aria-label": "Twitter",
              })}
              {renderSocialLink(facebook, <Facebook />, {
                "aria-label": "Facebook",
              })}
              {renderSocialLink(youtube, <Youtube />, {
                "aria-label": "Youtube",
              })}
            </Social>
          </Column>
        </Sub>
        <Copyright>{`© ${currentYear} URBAN ARMOR GEAR, LLC. ALL RIGHTS RESERVED.`}</Copyright>
      </Footer>
    </DesktopOnly>
  )
}

export default DesktopFooter
